import { get, put } from "./api";

/** One page and whether this staff member currently reaches it. */
export type UserPermission = {
  key: string;
  label: string;
  allowed: boolean;
};

export type UserPermissions = {
  userId: string;
  role: string;
  /**
   * False for admins and system admins, whose access this screen cannot change — an admin
   * administers restrictions, so allowing one to be restricted would let a head coach lock
   * themselves out of the screen that undoes it.
   */
  isRestrictable: boolean;
  permissions: UserPermission[];
};

/** Read a staff member's page access. Admin only. */
export const getUserPermissions = async (
  userId: string
): Promise<UserPermissions> => {
  return get<UserPermissions>(`/api/users/${userId}/permissions`);
};

/**
 * Set exactly which pages a staff member reaches. Anything omitted is withheld.
 *
 * A full replacement rather than a delta: two admins editing the same coach would otherwise
 * interleave into a state neither of them chose.
 */
export const updateUserPermissions = async (
  userId: string,
  allowed: string[]
): Promise<UserPermissions> => {
  return put<UserPermissions>(`/api/users/${userId}/permissions`, { allowed });
};

/** A role this organization can assign, with the copy its UI should show. */
export type AssignableRole = {
  key: string;
  label: string;
  description: string;
};

/**
 * The roles this organization can assign. Never includes `system_admin` — that is a platform role,
 * not an organization's to grant.
 */
export const getAssignableRoles = async (): Promise<AssignableRole[]> => {
  const response = await get<{ roles: AssignableRole[] }>(
    "/api/users/assignable-roles"
  );
  return response.roles;
};

/**
 * Change a staff member's role. Admin only.
 *
 * The API refuses to change your own role, to touch a platform account, or to demote an
 * organization's last administrator — a zero-admin organization cannot appoint its way back out,
 * since roles and page access are both admin-only.
 */
export const updateUserRole = async (
  userId: string,
  role: string
): Promise<{ id: string; role: string }> => {
  return put<{ id: string; role: string }>(`/api/users/${userId}/role`, { role });
};

/** One thing a user created, for the activity list on their detail view. */
export type UserActivityItem = {
  type: "prospect" | "note" | "call" | "event";
  description: string;
  occurredAt: string;
};

/**
 * What a staff member has been doing.
 *
 * **Derived from existing records, not an audit trail.** Assembled from the `created_by` columns
 * already on prospects, notes, calls and events, so it works over data already in the database —
 * but it can only show things a user *created*. Edits, deletions, and logins beyond the last one are
 * not recorded anywhere to report.
 */
export type UserActivity = {
  lastLoginAt: string | null;
  createdAt: string;
  counts: {
    prospects: number;
    notes: number;
    calls: number;
    events: number;
  };
  recent: UserActivityItem[];
};

export const getUserActivity = async (userId: string): Promise<UserActivity> => {
  return get<UserActivity>(`/api/users/${userId}/activity`);
};
